import { useEffect, useState } from "react";
import { Loader2, ImagePlus, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { NativeSelect, AuthImage } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export const STATUS_LABEL = { open: "Open", in_progress: "In progress", fixed: "Fixed", retest: "Retest", closed: "Closed", reopened: "Reopened" };
export const SEV_STYLE = { critical: "bg-rose-600 text-white", high: "bg-orange-100 text-orange-800", medium: "bg-amber-50 text-amber-800", low: "bg-slate-100 text-slate-700" };
const EMPTY = { title: "", test_case_id: "", severity: "medium", steps: "", expected: "", actual: "", device: "" };

export function NewBugDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [cases, setCases] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && !cases.length) api.get("/bugs/test-cases").then((r) => setCases(r.data)); }, [open, cases.length]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bugs", f);
      for (const file of files) { const fd = new FormData(); fd.append("file", file); await api.post(`/bugs/${data.id}/screenshots`, fd); }
      toast.success(`${data.code} logged`);
      setF(EMPTY); setFiles([]); onOpenChange(false); onCreated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-bug-dialog">
        <DialogHeader><DialogTitle className="font-display">Log a bug</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2 space-y-1"><Label>Title</Label><Input value={f.title} onChange={set("title")} placeholder="Deliver button does nothing on Android" data-testid="bug-title-input" /></div>
          <div className="space-y-1"><Label>Test case</Label>
            <NativeSelect value={f.test_case_id} onChange={set("test_case_id")} data-testid="bug-case-select">
              <option value="">Not linked / general</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.id} · {c.title}</option>)}
            </NativeSelect></div>
          <div className="space-y-1"><Label>Severity</Label>
            <NativeSelect value={f.severity} onChange={set("severity")} data-testid="bug-severity-select">
              {["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </NativeSelect></div>
          <div className="sm:col-span-2 space-y-1"><Label>Steps to reproduce</Label><Textarea value={f.steps} onChange={set("steps")} data-testid="bug-steps-input" /></div>
          <div className="space-y-1"><Label>Expected result</Label><Textarea value={f.expected} onChange={set("expected")} data-testid="bug-expected-input" /></div>
          <div className="space-y-1"><Label>Actual result</Label><Textarea value={f.actual} onChange={set("actual")} data-testid="bug-actual-input" /></div>
          <div className="space-y-1"><Label>Device / browser</Label><Input value={f.device} onChange={set("device")} placeholder="Redmi Note 12, Chrome" data-testid="bug-device-input" /></div>
          <div className="space-y-1"><Label>Screenshots (max 3)</Label>
            <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-[#E7E2D6] px-3 text-sm" data-testid="bug-screenshot-label">
              <ImagePlus className="h-4 w-4" />{files.length ? `${files.length} selected` : "Choose images"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles([...e.target.files].slice(0, 3))} data-testid="bug-screenshot-input" />
            </label></div>
        </div>
        <DialogFooter><Button disabled={busy || !f.title.trim()} onClick={submit} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid="bug-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log bug"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NEXT = { admin: ["in_progress", "fixed", "retest", "closed", "reopened"], qa: ["retest", "reopened", "closed"] };

export function BugDetailDialog({ bugId, onClose, onChanged }) {
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { setB(null); if (bugId) api.get(`/bugs/${bugId}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))); }, [bugId]);
  const act = async (fn) => { try { setB((await fn()).data); setText(""); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!bugId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bug-detail-dialog">
        {!b ? <><DialogTitle className="sr-only">Loading bug</DialogTitle><Loader2 className="mx-auto h-6 w-6 animate-spin" /></> : <>
          <DialogHeader><DialogTitle className="font-display">{b.code} · {b.title}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${SEV_STYLE[b.severity]}`}>{b.severity}</span>
            <span className="rounded-full bg-[#FAF4E8] px-2 py-0.5 font-semibold" data-testid="bug-detail-status">{STATUS_LABEL[b.status]}</span>
            <span className="text-muted-foreground">{b.test_case_id || "General"} · {b.module} · by {b.reporter_name} · {fmtDateTime(b.created_at)}</span>
          </div>
          {[["Steps", b.steps], ["Expected", b.expected], ["Actual", b.actual], ["Device", b.device]].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="text-sm"><div className="text-xs font-semibold uppercase text-muted-foreground">{k}</div><div className="whitespace-pre-wrap">{v}</div></div>
          ))}
          {b.screenshots.length > 0 && <div className="grid grid-cols-3 gap-2" data-testid="bug-screenshots">{b.screenshots.map((s, i) => <AuthImage key={i} path={`/bugs/${b.id}/screenshots/${i}`} className="h-28 w-full rounded-lg" alt="Screenshot" testid={`bug-shot-${i}`} />)}</div>}
          <div className="flex flex-wrap gap-2" data-testid="bug-status-actions">
            {(NEXT[user?.role] || []).filter((s) => s !== b.status).map((s) => (
              <Button key={s} size="sm" variant="outline" onClick={() => act(() => api.post(`/bugs/${b.id}/status`, { status: s, comment: text }))} data-testid={`bug-move-${s}`}>{STATUS_LABEL[s]}</Button>
            ))}
          </div>
          <div className="space-y-2 rounded-xl bg-[#FBF9F4] p-3" data-testid="bug-comments">
            {b.comments.map((c, i) => <div key={i} className="text-sm"><b>{c.by}</b> <span className="text-xs text-muted-foreground">{fmtDateTime(c.at)}</span><div>{c.text}</div></div>)}
            <div className="flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment" data-testid="bug-comment-input" />
              <Button size="icon" disabled={!text.trim()} onClick={() => act(() => api.post(`/bugs/${b.id}/comments`, { text }))} data-testid="bug-comment-send"><Send className="h-4 w-4" /></Button></div>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="bug-history">{b.history.map((h) => `${STATUS_LABEL[h.to]} by ${h.by} (${fmtDateTime(h.at)})`).join(" → ")}</div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
